import { TConfidenceTone } from './SegmentedMeter';
import { TBiometricConfidenceLevel, TDocumentConfidenceLevel } from './types';

/**
 * The two axes have **different ladders**, and that is the design, not an accident.
 *
 * Documents run over four steps because the axis has four reachable confidence levels above zero:
 * Low, Substantial, High and Very High. Biometric runs over two even though it has three above zero,
 * because `InProgress` and `Low` occupy the same first step: the count of steps is the count of
 * distinct positions, not the count of levels. Each ladder is sized so a full meter means "as good
 * as this check gets" on both, rather than leaving a permanently unreachable step on the shorter
 * axis.
 *
 * The consequence to keep in mind when reading the tables below: a level name shared by both axes
 * does **not** share a position. `High` is three of four on documents and two of two on biometric.
 * That is why the mapping is per axis and why each axis has its own meter component.
 */
export const DOCUMENT_LADDER_STEPS = 4;
export const BIOMETRIC_LADDER_STEPS = 2;

export type TLadderPosition = {
  /** How many of that axis's steps are filled. */
  filled: number;
  /** The colour those filled steps take. */
  tone: TConfidenceTone;
};

/**
 * Documents, four steps.
 *
 * `NotCompleted` renders the full ladder unfilled rather than no ladder at all, so a reader always
 * sees how many steps exist. Its tone is never used at zero fill. On screen this level reads
 * "Under review" (see `DOCUMENT_LEVEL_NAMES` below); the enum value stays `NotCompleted` because that
 * is the name the biometric axis's equivalent zero state also uses, even though the two display
 * different text.
 */
const DOCUMENT_LADDER: Record<TDocumentConfidenceLevel, TLadderPosition> = {
  NotCompleted: { filled: 0, tone: 'none' },
  Low: { filled: 1, tone: 'low' },
  Substantial: { filled: 2, tone: 'substantial' },
  High: { filled: 3, tone: 'high' },
  VeryHigh: { filled: 4, tone: 'high' },
};

/**
 * Biometric, two steps.
 *
 * **Fill count does not imply tone**, and this axis is where that matters most: `InProgress` and
 * `Low` both fill one of two, the first in neutral grey because the check ran and returned nothing
 * yet, the second in red because it returned a poor result. Same height, opposite meaning. Storing
 * the tone separately is what keeps those two distinguishable.
 */
const BIOMETRIC_LADDER: Record<TBiometricConfidenceLevel, TLadderPosition> = {
  NotCompleted: { filled: 0, tone: 'none' },
  InProgress: { filled: 1, tone: 'pending' },
  Low: { filled: 1, tone: 'low' },
  High: { filled: 2, tone: 'high' },
};

/**
 * How each document level is named on screen.
 *
 * **Per axis, not shared.** `NotCompleted` is the case that proves why: it is a level both axes have,
 * but it reads "Under review" here and "Not completed" on biometric (see `BIOMETRIC_LEVEL_NAMES`
 * below). A single name table keyed by the union type cannot express that, which is why there are two
 * tables rather than one.
 *
 * Sentence case throughout, matching every other heading and level name in the design.
 */
const DOCUMENT_LEVEL_NAMES: Record<TDocumentConfidenceLevel, string> = {
  NotCompleted: 'Under review',
  Low: 'Low confidence',
  Substantial: 'Substantial confidence',
  High: 'High confidence',
  VeryHigh: 'Very high confidence',
};

/**
 * How each biometric level is named on screen.
 *
 * `NotCompleted` reads "Not completed" here, not "Under review": that wording belongs to the document
 * axis alone. The guidance prose that follows the name does differ by axis too, but that belongs to
 * the cards, not here.
 */
const BIOMETRIC_LEVEL_NAMES: Record<TBiometricConfidenceLevel, string> = {
  NotCompleted: 'Not completed',
  InProgress: 'In progress',
  Low: 'Low confidence',
  High: 'High confidence',
};

/** Where a document level sits on the four-step document ladder. */
export function documentLadderPositionOf(level: TDocumentConfidenceLevel): TLadderPosition {
  return DOCUMENT_LADDER[level];
}

/** Where a biometric level sits on the two-step biometric ladder. */
export function biometricLadderPositionOf(level: TBiometricConfidenceLevel): TLadderPosition {
  return BIOMETRIC_LADDER[level];
}

/** What a document level is called on screen. */
export function documentConfidenceLevelName(level: TDocumentConfidenceLevel): string {
  return DOCUMENT_LEVEL_NAMES[level];
}

/** What a biometric level is called on screen. */
export function biometricConfidenceLevelName(level: TBiometricConfidenceLevel): string {
  return BIOMETRIC_LEVEL_NAMES[level];
}
